import {
  FilterInfoOperator,
  FormattingOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import {
  type LegacyComponentOrReferenceFormattingRule,
  legacyTypeNamesToTypeNames,
} from '../normalizeRule';

export type SelectOption = {
  value: string;
  label: string;
};

export type ValueTransformationResult = {
  newValue: ComponentOrReferenceFormattingRule['value'];
  warnings: string[];
};

export type TypeNameTransformationResult = {
  newTypeNames: ComponentOrReferenceFormattingRule['typeNames'];
  warnings: string[];
};

/**
 * Transforms the value of a rule based on the new comparator.
 * Handles special cases like BOOLEAN, IS_EMPTY and SELECT fields.
 */
export const transformValue = (
  oldRule: ComponentOrReferenceFormattingRule,
  newComparator: FormattingOperator,
  selectOptions?: SelectOption[],
  handleValueTransformation?: (
    rule: ComponentOrReferenceFormattingRule,
    options?: SelectOption[]
  ) => ValueTransformationResult
): ValueTransformationResult => {
  const warnings: string[] = [];
  const { value, comparator } = oldRule;

  if (handleValueTransformation) {
    return handleValueTransformation(oldRule, selectOptions);
  }

  // Handle BOOLEAN fields - transform based on value
  if (comparator === FilterInfoOperator.BOOLEAN) {
    // Old system: value was true/false, comparator was always BOOLEAN
    // New system: comparator determines checked/unchecked, value is always true
    return {
      newValue: true, // Value is always true for BOOLEAN/NOT_BOOLEAN
      warnings,
    };
  }

  // Handle IS_EMPTY operator - value should be null
  if (newComparator === FormattingRuleOperator.IS_EMPTY) {
    if (value !== null) {
      warnings.push('Value changed to null for IS_EMPTY operator');
    }
    return { newValue: null, warnings };
  }

  // Default: keep value as-is
  return { newValue: value, warnings };
};

// TODO(typeName-migration): Simplify by replacing legacyTypeNamesToTypeNames(oldRule) with oldRule.typeNames
// once BE has migrated all v0 rules to use typeNames instead of typeName.
export const transformTypeName = (
  oldRule: LegacyComponentOrReferenceFormattingRule,
  selectOptions?: SelectOption[],
  handleTypeNameTransformation?: (
    rule: LegacyComponentOrReferenceFormattingRule,
    options?: SelectOption[]
  ) => TypeNameTransformationResult
): TypeNameTransformationResult => {
  if (handleTypeNameTransformation) {
    return handleTypeNameTransformation(oldRule, selectOptions);
  }

  return {
    newTypeNames: legacyTypeNamesToTypeNames(oldRule),
    warnings: [],
  };
};
